import { useCallback, useReducer, useRef } from 'react';

/** Keep something rendered after it stops being "present" so it can animate
 *  out. Modal open state lives in the URL, so the moment you navigate away the
 *  selected entity (and tab) become undefined; this latches the last defined
 *  value — the whole props bundle, not just the entity, so a modal doesn't
 *  flip to its default tab mid-exit.
 *
 *    const p = usePresence(selected ? { scraper: selected, tab } : undefined);
 *    {p.item && <Detail {...p.item} open={p.open} onExited={p.onExited} />}
 *
 *  `onExited` is a no-op if the value came back during the exit, so
 *  reopening mid-animation just retargets the transition. */
export function usePresence<T>(value: T | undefined): {
  item: T | undefined;
  open: boolean;
  onExited: () => void;
} {
  const open = value !== undefined;
  const latched = useRef<T | undefined>(value);
  const openRef = useRef(open);
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  openRef.current = open;
  if (open) latched.current = value;

  const onExited = useCallback(() => {
    if (openRef.current) return;
    latched.current = undefined;
    rerender();
  }, []);

  return { item: open ? value : latched.current, open, onExited };
}
